import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Receipt, CheckCircle2, Clock, AlertTriangle, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { superAdminAPI } from '../../services/api';

const ZatcaOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await superAdminAPI.getZatcaOverview();
      setData(response.data?.data || response.data);
    } catch (error) {
      toast.error('فشل في تحميل بيانات ZATCA');
    } finally {
      setLoading(false);
    }
  };

  const statusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'active':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'pending':
      case 'in_progress':
        return <Clock className="w-5 h-5 text-amber-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-40 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
              <div className="h-4 w-20 bg-gray-200 rounded mb-3" />
              <div className="h-8 w-12 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const tenants = data?.tenants || data?.branches || [];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">ZATCA Overview</h1>
        <p className="text-sm text-gray-500 mt-1">نظرة عامة على حالة الربط مع هيئة الزكاة والضريبة</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            <p className="text-sm text-gray-500">مكتمل</p>
          </div>
          <p className="text-3xl font-bold text-gray-800">{stats.completed || 0}</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-amber-500" />
            <p className="text-sm text-gray-500">قيد التنفيذ</p>
          </div>
          <p className="text-3xl font-bold text-gray-800">{stats.inProgress || stats.pending || 0}</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <p className="text-sm text-gray-500">غير مبدوء</p>
          </div>
          <p className="text-3xl font-bold text-gray-800">{stats.notStarted || 0}</p>
        </motion.div>
      </div>

      {/* Tenants List */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">حالة ZATCA لكل مختبر</h2>
        </div>
        {tenants.length > 0 ? (
          <div className="divide-y divide-gray-50">
            {tenants.map((item, i) => (
              <div key={item._id || i} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-gray-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{item.tenantName || item.name}</p>
                    <p className="text-xs text-gray-400">
                      {item.branchCount != null ? `${item.branchCount} فروع` : item.branchName || ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {statusIcon(item.status || item.zatcaStatus)}
                  <span className="text-sm text-gray-600">
                    {item.status === 'completed' || item.zatcaStatus === 'completed' ? 'مكتمل' :
                     item.status === 'in_progress' || item.zatcaStatus === 'in_progress' ? 'قيد التنفيذ' :
                     item.status === 'pending' || item.zatcaStatus === 'pending' ? 'معلق' : 'غير مبدوء'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-400 text-sm">
            <Receipt className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            لا توجد بيانات
          </div>
        )}
      </div>
    </div>
  );
};

export default ZatcaOverview;
